/**
 * © 2025 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 */
import { NgFor, NgIf } from "@angular/common";
import {
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  output,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from "@angular/forms";
import { SubscriptionsService } from "../services/user/subscriptions.service";

@Component({
  selector: "app-parameter-table",
  standalone: true,
  imports: [NgFor, NgIf, ReactiveFormsModule],
  templateUrl: "./parameter-table.component.html",
  styleUrl: "./parameter-table.component.css",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ParameterTableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() dashboardId!: string;
  @Input() initialParameterValues?: any; // Add input for initial parameter values in edit mode
  @Input() delayInitialization: boolean = false; // Control when to initialize parameters
  @Input() requiredParameters: string[] = []; // List of required parameter names (case-insensitive)
  @Input() invalidParameters: string[] = []; // List of parameter names that have validation errors
  onParameterChange = output<{}>();
  parameterNames: string[] = [];
  parameterConfigs: any = { parameters_from_definition: [] }; // Initialize with empty array to avoid template errors
  column: any;
  subscriptionsServices: SubscriptionsService = inject(SubscriptionsService);
  parametersForm!: FormGroup;
  parameters: { [key: string]: any } = {};
  isLoading: boolean = true;
  existingValues: any = {}; // Store form values before reset
  private isInitialized: boolean = false;
  private shouldInitializeParameters: boolean = false;

  // Cache for dropdown items — prevents real-time reordering during selection
  dropdownItemsMap: Map<string, any[]> = new Map();
  // Controls that have pending selections and need re-sorting when dropdown closes
  private pendingSortControls: Set<string> = new Set();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.pendingSortControls.size === 0) return;
    // Sort pending dropdowns when user clicks outside any f-dropdown (i.e. closes it)
    const path = event.composedPath();
    const isInsideDropdown = path.some((el: any) =>
      el.tagName?.toLowerCase() === 'f-dropdown'
    );
    if (!isInsideDropdown) {
      this.pendingSortControls.forEach(controlName => {
        this.sortDropdownItems(controlName);
      });
      this.pendingSortControls.clear();
    }
  }

  constructor(private parentFormDirective: FormGroupDirective) {}

  ngOnInit(): void {
    this.parametersForm = this.parentFormDirective.form;

    // Store current form values for edit mode without destroying anything
    this.existingValues = this.initialParameterValues || {};

    // Mark that we should initialize parameters after view init
    this.shouldInitializeParameters = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["initialParameterValues"]) {
      this.existingValues = this.initialParameterValues || {};

      if (this.parametersForm && this.parameterNames.length > 0) {
        this.applyExistingValues();
      }
    }
  }

  ngAfterViewInit(): void {
    // Use AfterViewInit to ensure all components are properly initialized
    if (this.shouldInitializeParameters) {
      if (this.delayInitialization) {
        // For edit mode, wait for the main form to be fully populated
        this.waitForFormStability().then(() => {
          this.initializeParameters();
        });
      } else {
        // For create mode, initialize immediately
        this.initializeParameters();
      }
    }
  }

  private async waitForFormStability(): Promise<void> {
    // Wait for form to have the expected number of controls (indicating main form is ready)
    const expectedMinimumControls = 9; // Main form controls

    return new Promise((resolve) => {
      const checkFormStability = () => {
        const currentControlCount = Object.keys(
          this.parametersForm.controls
        ).length;

        if (currentControlCount >= expectedMinimumControls) {
          resolve();
        } else {
          // Use requestAnimationFrame for better performance than setTimeout
          requestAnimationFrame(checkFormStability);
        }
      };

      checkFormStability();
    });
  }

  private initializeParameters(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.isLoading = true;

    this.subscriptionsServices.getParameterConfigs(this.dashboardId).subscribe({
      next: (response) => {
        // Update parameterConfigs without changing reference to avoid change detection errors
        this.parameterConfigs.parameters_from_definition =
          response.parameters_from_definition || [];

        // Override options for pAsOfDate to match backend expectations
        if (this.parameterConfigs?.parameters_from_definition) {
          this.parameterConfigs.parameters_from_definition.forEach(
            (param: any) => {
              if (
                param.StringParameterDeclaration &&
                param.StringParameterDeclaration.Name.toLowerCase() ===
                  "pasofdate"
              ) {
                param.options = [
                  "Current Date",
                  "First Day of Month",
                  "Last Day of Month",
                  "First Day of Quarter",
                  "Last Day of Quarter",
                  "First Day of Year",
                  "Last Day of Year",
                ];
              }
            }
          );
        }

        this.initialTable();

        // Re-apply any existing values immediately after form controls are created
        this.applyExistingValues();

        this.isLoading = false;
      },
      error: (error) => {
        console.error("Error fetching parameter configs:", error);
        this.isLoading = false;
      },
    });
  }

  initialTable() {
    if (
      this.parameterConfigs &&
      this.parameterConfigs.parameters_from_definition
    ) {
      this.parameterConfigs.parameters_from_definition.forEach(
        (element: {
          DateTimeParameterDeclaration: { Name: any };
          StringParameterDeclaration: {
            Name: any;
            DefaultValues: any;
            ParameterValueType: any;
          };
          IntegerParameterDeclaration: any;
          options: any;
        }) => {
          try {
            if (element.DateTimeParameterDeclaration) {
              const controlName = element.DateTimeParameterDeclaration.Name;
              if (!this.parametersForm.contains(controlName)) {
                this.parametersForm.addControl(
                  controlName,
                  new FormControl(Date())
                );
              }
              this.parameterNames.push(controlName);
            } else if (element.StringParameterDeclaration) {
              const controlName = element.StringParameterDeclaration.Name;
              if (!this.parametersForm.contains(controlName)) {
                if (
                  element.StringParameterDeclaration.ParameterValueType ===
                  "MULTI_VALUED"
                ) {
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl([])
                  );
                } else {
                  // SINGLE_VALUED StringParameterDeclaration
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl("")
                  );
                }
              }
              this.parameterNames.push(controlName);
            } else if (element.IntegerParameterDeclaration) {
              const controlName = element.IntegerParameterDeclaration.Name;
              if (!this.parametersForm.contains(controlName)) {
                if (
                  element.IntegerParameterDeclaration.ParameterValueType ===
                  "MULTI_VALUED"
                ) {
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl([])
                  );
                } else {
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl("")
                  );
                }
              }
              this.parameterNames.push(controlName);
            }
          } catch (error) {
            console.error(`Error adding control for element:`, element, error);
          }
        }
      );
    }
    this.updateParameters();
  }

  getStringDropdownItems(column: any): any[] {
    const controlName = column?.StringParameterDeclaration?.Name;
    if (!controlName) return [];

    // Return cached list — only rebuilt when dropdown closes or on first load
    if (this.dropdownItemsMap.has(controlName)) {
      return this.dropdownItemsMap.get(controlName)!;
    }

    const selectedValues = this.getSelectedValues(controlName);
    const selectedLookup = new Set(
      selectedValues.map((value) => this.normalizeForCompare(value))
    );
    const options = Array.isArray(column?.options) ? column.options : [];

    const items = options
      .map((option: any, index: number) => {
        const name = this.extractOptionName(option);
        const optionValue = this.extractOptionValue(option);
        if (!name) return null;
        return {
          uuid: `${controlName}-${index}`,
          name,
          selected:
            selectedLookup.has(this.normalizeForCompare(name)) ||
            selectedLookup.has(this.normalizeForCompare(optionValue)),
          disabled: false,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return 0;
      }) as any[];

    this.dropdownItemsMap.set(controlName, items);
    return items;
  }

  getIntegerDropdownItems(column: any): any[] {
    const controlName = column?.IntegerParameterDeclaration?.Name;
    if (!controlName) return [];

    // Return cached list — only rebuilt when dropdown closes or on first load
    if (this.dropdownItemsMap.has(controlName)) {
      return this.dropdownItemsMap.get(controlName)!;
    }

    const selectedValues = this.getSelectedValues(controlName);
    const selectedLookup = new Set(
      selectedValues.map((value) => this.normalizeForCompare(value))
    );
    const options = Array.isArray(column?.options) ? column.options : [];

    const items = (Array.isArray(options) ? options : [])
      .map((option: any, index: number) => {
        const name = this.extractOptionName(option);
        const optionValue = this.extractOptionValue(option);
        if (!name) return null;
        return {
          uuid: `${controlName}-${index}`,
          name,
          selected:
            selectedLookup.has(this.normalizeForCompare(name)) ||
            selectedLookup.has(this.normalizeForCompare(optionValue)),
          disabled: false,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return 0;
      }) as any[];

    this.dropdownItemsMap.set(controlName, items);
    return items;
  }

  onDropdownValueChanged(
    event: Event,
    controlName: string,
    isMultiSelect: boolean
  ): void {
    this.applyDropdownEventValue(event, controlName, isMultiSelect);
    // Update selected state in cached items without reordering
    this.updateDropdownItemsSelected(controlName);
    if (isMultiSelect) {
      // Defer sorting until the dropdown closes (detected via document click)
      this.pendingSortControls.add(controlName);
    } else {
      // Single-select: dropdown auto-closes, sort immediately
      this.sortDropdownItems(controlName);
    }
  }

  private updateDropdownItemsSelected(controlName: string): void {
    const items = this.dropdownItemsMap.get(controlName);
    if (!items) return;
    const selectedValues = this.getSelectedValues(controlName);
    const selectedLookup = new Set(
      selectedValues.map((v) => this.normalizeForCompare(v))
    );
    // Update selected state in-place (new objects, new array reference for change detection)
    const updated = items.map((item: any) => ({
      ...item,
      selected: selectedLookup.has(this.normalizeForCompare(item.name)),
    }));
    this.dropdownItemsMap.set(controlName, updated);
  }

  private sortDropdownItems(controlName: string): void {
    const items = this.dropdownItemsMap.get(controlName);
    if (!items) return;
    const sorted = [...items].sort((a: any, b: any) => {
      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;
      return 0;
    });
    this.dropdownItemsMap.set(controlName, sorted);
  }

  private getSelectedValues(controlName?: string): string[] {
    if (!controlName) {
      return [];
    }

    const value = this.parametersForm.get(controlName)?.value;
    const parsedValues = this.parseToValueList(value);
    return parsedValues
      .map((item: any) => this.extractOptionName(item))
      .map((item: string) => this.cleanStoredValue(item))
      .filter((item: string) => !!item);
  }

  private parseToValueList(value: any): any[] {
    if (value === null || value === undefined) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        const jsonLike = trimmed
          .replace(/'/g, '"')
          .replace(/\bNone\b/g, "null")
          .replace(/\bTrue\b/g, "true")
          .replace(/\bFalse\b/g, "false");

        try {
          const parsed = JSON.parse(jsonLike);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          const fallback = trimmed
            .slice(1, -1)
            .split(",")
            .map((item) => this.cleanStoredValue(item))
            .filter((item) => !!item);
          return fallback;
        }
      }

      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((item) => this.cleanStoredValue(item))
          .filter((item) => !!item);
      }

      return [this.cleanStoredValue(trimmed)];
    }

    return [value];
  }

  private extractOptionName(option: any): string {
    if (option === null || option === undefined) {
      return "";
    }

    if (typeof option === "string" || typeof option === "number") {
      return this.cleanStoredValue(String(option));
    }

    if (typeof option === "object") {
      if (option.name !== null && option.name !== undefined) {
        return this.cleanStoredValue(String(option.name));
      }
      if (option.value !== null && option.value !== undefined) {
        return this.cleanStoredValue(String(option.value));
      }
    }

    return "";
  }

  private extractOptionValue(option: any): string {
    if (option === null || option === undefined) {
      return "";
    }

    if (typeof option === "object" && option.value !== null && option.value !== undefined) {
      return this.cleanStoredValue(String(option.value));
    }

    return this.extractOptionName(option);
  }

  private cleanStoredValue(value: string): string {
    const trimmed = (value || "").trim();
    return trimmed.replace(/^['\"]|['\"]$/g, "").trim();
  }

  private normalizeForCompare(value: string): string {
    return this.cleanStoredValue(value).toLowerCase();
  }

  private applyDropdownEventValue(
    event: Event,
    controlName: string,
    isMultiSelect: boolean
  ): void {
    const control = this.parametersForm.get(controlName);
    if (!control) {
      return;
    }

    const detail = (event as CustomEvent<any>)?.detail;
    const selected = this.extractSelectedFromDetail(detail);

    if (isMultiSelect) {
      if (Array.isArray(selected)) {
        const values = selected
          .map((item: any) => this.extractOptionName(item))
          .filter((value: string) => !!value);
        control.setValue(values);
      } else if (selected && typeof selected === "object") {
        const toggledName = this.extractOptionName(selected);
        const toggledSelected =
          typeof selected.selected === "boolean"
            ? selected.selected
            : typeof detail?.selected === "boolean"
              ? detail.selected
              : true;
        const current = this.getSelectedValues(controlName);
        const next = toggledSelected
          ? Array.from(new Set([...current, toggledName]))
          : current.filter(
              (v) =>
                this.normalizeForCompare(v) !==
                this.normalizeForCompare(toggledName)
            );
        control.setValue(next.filter((v) => !!v));
      } else if (selected === null || selected === undefined || selected === "") {
        control.setValue([]);
      } else {
        const selectedItems = [selected];
        const values = selectedItems
          .map((item: any) => this.extractOptionName(item))
          .filter((value: string) => !!value);
        control.setValue(values);
      }
    } else {
      control.setValue(this.extractOptionName(selected));
    }

    this.updateParameters();
  }

  private extractSelectedFromDetail(detail: any): any {
    if (!detail) {
      return detail;
    }

    return (
      detail.currentItemSelected ??
      detail.currentItemsSelected ??
      detail.selectedItems ??
      detail.values ??
      detail.value ??
      detail.item ??
      detail
    );
  }

  updateParameters(): void {
    this.parameterNames.forEach((parameterName) => {
      const control = this.parametersForm.get(parameterName);
      if (control) {
        const value = control.value;

        this.parameters[parameterName] = value;
      } else {
      }
    });

    this.onParameterChange.emit(this.parameters);
  }

  private applyExistingValues(): void {
    if (!this.existingValues || Object.keys(this.existingValues).length === 0) {
      return;
    }

    Object.keys(this.existingValues).forEach((rawKey) => {
      const formKey = Object.keys(this.parametersForm.controls).find(
        (controlName) =>
          controlName.toLowerCase() === String(rawKey).toLowerCase()
      );

      if (!formKey) {
        return;
      }

      const value = this.existingValues[rawKey];
      if (value === null || value === undefined) {
        return;
      }

      this.parametersForm.get(formKey)?.setValue(value);
    });

    // Refresh cached dropdown items to reflect applied values
    this.parameterNames.forEach((controlName) => {
      if (this.dropdownItemsMap.has(controlName)) {
        this.updateDropdownItemsSelected(controlName);
        this.sortDropdownItems(controlName);
      }
    });

    this.updateParameters();
  }

  isParameterRequired(parameterName: string): boolean {
    if (!this.requiredParameters || this.requiredParameters.length === 0) {
      return false;
    }
    const normalizedParamName = parameterName.toLowerCase();
    return this.requiredParameters.some(reqParam => reqParam.toLowerCase() === normalizedParamName);
  }

  hasError(parameterName: string): boolean {
    if (!this.invalidParameters || this.invalidParameters.length === 0) {
      return false;
    }
    const normalizedParamName = parameterName.toLowerCase();
    return this.invalidParameters.some(invalidParam => invalidParam.toLowerCase() === normalizedParamName);
  }

  getErrorMessage(parameterName: string): string {
    return this.hasError(parameterName) ? 'Please select a value' : '';
  }
}